import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin,
  ArrowRight, Truck, Package, Globe
} from 'lucide-react';

// --- STYLED COMPONENTS ---

const FooterContainer = styled.footer`
  background-color: #0f172a; /* Deep Slate - Matches Dark Theme */
  color: white;
  padding: 80px 0 30px;
  border-top: 1px solid #1e293b;
  font-family: 'Inter', sans-serif;
`;

const FooterContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 60px;
  margin-bottom: 80px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 40px;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 50px;
  }
`;

const BrandColumn = styled.div`
  .logo {
    font-size: 1.8rem;
    font-weight: 800;
    margin-bottom: 20px;
    letter-spacing: -0.03em;
    display: block;
    color: white;
    text-decoration: none;

    span {
      color: #6366f1; /* Indigo */
    }
  }

  .description {
    color: #94a3b8; /* Slate-400 */
    line-height: 1.7;
    margin-bottom: 32px;
    max-width: 350px;
    font-size: 1rem;
  }
`;

const SocialRow = styled.div`
  display: flex;
  gap: 16px;
`;

const SocialButton = styled(motion.a)`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    background: #6366f1;
    color: white;
    transform: translateY(-3px);
    border-color: #6366f1;
    box-shadow: 0 10px 20px -5px rgba(99, 102, 241, 0.4);
  }
`;

const FooterColumn = styled.div`
  h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: white;
    margin-bottom: 24px;
    letter-spacing: 0.02em;
  }
`;

const LinkList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterLink = styled.li`
  margin-bottom: 16px;

  a {
    color: #94a3b8;
    text-decoration: none;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 0.95rem;

    &:hover {
      color: #6366f1;
      transform: translateX(4px);
    }
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  color: #94a3b8;
  font-size: 0.95rem;
  line-height: 1.5;

  svg {
    color: #6366f1;
    flex-shrink: 0;
    margin-top: 3px;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #1e293b;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto 30px;
`;

const FooterBottom = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  p {
    color: #64748b;
    font-size: 0.9rem;
  }

  .legal-links {
    display: flex;
    gap: 24px;

    a {
      color: #64748b;
      font-size: 0.9rem;
      text-decoration: none;
      transition: color 0.2s;

      &:hover {
        color: white;
      }
    }
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <FooterContent>
        {/* BRAND COLUMN */}
        <BrandColumn>
          <Link to="/" className="logo">
            SDD <span>LOGISTICS</span>
          </Link>
          <p className="description">
            Connecting 1000+ businesses across every pincode in India.
            Reliable warehousing and logistics solutions powered by technology and 15+ years of expertise.
          </p>
          <SocialRow>
            <SocialButton href="#" aria-label="Facebook" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Facebook size={20} />
            </SocialButton>
            <SocialButton href="#" aria-label="Twitter" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Twitter size={20} />
            </SocialButton>
            <SocialButton href="#" aria-label="LinkedIn" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Linkedin size={20} />
            </SocialButton>
            <SocialButton href="#" aria-label="Instagram" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <Instagram size={20} />
            </SocialButton>
          </SocialRow>
        </BrandColumn>

        {/* SERVICES COLUMN */}
        <FooterColumn>
          <h3>Our Solutions</h3>
          <LinkList>
            <FooterLink>
              <Link to="/services">
                <Truck size={16} /> Logistics Management
              </Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services">
                <Package size={16} /> Warehousing
              </Link>
            </FooterLink>
            <FooterLink>
              <Link to="/services">
                <Globe size={16} /> Carry & Forwarding
              </Link>
            </FooterLink>
            <FooterLink>
              <Link to="/tracking">
                <ArrowRight size={16} /> Track Shipment
              </Link>
            </FooterLink>
          </LinkList>
        </FooterColumn>

        {/* LINKS COLUMN */}
        <FooterColumn>
          <h3>Company</h3>
          <LinkList>
            <FooterLink><Link to="/">Home</Link></FooterLink>
            <FooterLink><Link to="/about">About Us</Link></FooterLink>
            <FooterLink><Link to="/careers">Careers</Link></FooterLink>
            <FooterLink><Link to="/contact">Contact</Link></FooterLink>
            <FooterLink><Link to="/dashboard">Partner Login</Link></FooterLink>
          </LinkList>
        </FooterColumn>

        {/* CONTACT COLUMN */}
        <FooterColumn>
          <h3>Contact Us</h3>
          <ContactList>
            <ContactItem>
              <MapPin size={20} />
              <span>Barddhaman, West Bengal,<br />India - 713101</span>
            </ContactItem>
            <ContactItem>
              <Phone size={20} />
              <span>+91 7029141680</span>
            </ContactItem>
            <ContactItem>
              <Mail size={20} />
              <span>info@sddlogistics.com</span>
            </ContactItem>
          </ContactList>
        </FooterColumn>
      </FooterContent>

      <Divider />

      <FooterBottom>
        <p>&copy; 2020-{currentYear} SDD LOGISTICS. All rights reserved.</p>
        <div className="legal-links">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <Link to="/sitemap">Sitemap</Link>
        </div>
      </FooterBottom>
    </FooterContainer>
  );
};

export default Footer;